import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { MaskedLine, EASE } from "@/components/Motion";
import HeroMontage from "@/components/HeroMontage";
import { SITE, buildWhatsAppLink } from "@/data/site";
import { useLang, u } from "@/data/i18n";

const A = `${process.env.PUBLIC_URL}/assets`;

export default function Hero() {
  const { lang } = useLang();
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 700], [0, -90]);
  const yLeaf = useTransform(scrollY, [0, 700], [0, 60]);
  const yBgText = useTransform(scrollY, [0, 700], [0, 120]);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="noise-overlay relative min-h-screen bg-leaf text-white overflow-hidden flex flex-col justify-center"
    >
      {/* Giant background word */}
      <motion.span
        style={{ y: yBgText }}
        aria-hidden
        className="text-stroke-white pointer-events-none select-none absolute -bottom-6 md:bottom-0 left-0 font-display font-black text-[22vw] leading-none tracking-tighter whitespace-nowrap"
      >
        LOVELY LEAFS
      </motion.span>

      {/* Floating manufacturing montage — subtle 3D parallax moment */}
      <motion.div
        style={{ y: yImg }}
        initial={{ opacity: 0, x: 80, rotate: 4 }}
        animate={{ opacity: 1, x: 0, rotate: 1.5 }}
        transition={{ duration: 1.4, delay: 0.9, ease: EASE }}
        className="hidden md:block absolute right-[5%] top-[15%] w-[300px] lg:w-[360px] z-30"
      >
        <HeroMontage />
      </motion.div>

      {/* Floating leaf illustration — framed spec card */}
      <motion.div
        style={{ y: yLeaf }}
        initial={{ opacity: 0, rotate: -14, scale: 0.8, x: -40 }}
        animate={{ opacity: 1, rotate: -6, scale: 1, x: 0 }}
        transition={{ duration: 1.4, delay: 1.3, ease: EASE }}
        className="pointer-events-none absolute left-[2%] bottom-[3%] w-52 md:w-72 hidden sm:block"
      >
        <div className="bg-white p-2 shadow-2xl border border-white/20">
          <img src={`${A}/leaf-meal-wide.jpg`} alt="Full South Indian meals served on Lovely Leafs paper banana leaf" className="w-full" />
          <p className="text-leaf text-[8px] tracking-[0.18em] uppercase font-bold pt-2 pb-1 px-1 whitespace-nowrap">
            Full meals on Lovely Leafs · Meals Leaf size
          </p>
        </div>
      </motion.div>

      <div className="relative z-20 max-w-[1500px] mx-auto w-full px-5 md:px-10 pt-32 pb-40 md:pt-36">
        <MaskedLine delay={0.1}>
          <span className="inline-flex items-center gap-3 border border-sun/50 text-sun text-[11px] md:text-xs tracking-[0.3em] uppercase font-bold px-4 py-2">
            {lang === "ta" ? u(lang, "hero_badge") : `Eco Friendly Food Packaging · Est. ${SITE.since}`}
          </span>
        </MaskedLine>

        <h1 className="mt-8 font-display font-black tracking-tighter leading-[0.95] text-[13vw] sm:text-6xl md:text-7xl lg:text-[6.2rem]">
          <MaskedLine delay={0.25}>{u(lang, "hero_l1")}</MaskedLine>
          <MaskedLine delay={0.4}>
            <span>
              {u(lang, "hero_l2a")}
              <em className="font-serif italic font-medium text-sun">{u(lang, "hero_l2b")}</em>
            </span>
          </MaskedLine>
          <MaskedLine delay={0.55}>{u(lang, "hero_l3")}</MaskedLine>
        </h1>

        <div className="mt-10 max-w-xl">
          <MaskedLine delay={0.75}>
            <p className="text-white/70 text-base md:text-lg leading-relaxed">{u(lang, "hero_sub")}</p>
          </MaskedLine>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            data-testid="hero-enquire-btn"
            href={buildWhatsAppLink("Hello Lovely Foodpacks! I'd like to discuss a bulk / export order.")}
            target="_blank"
            rel="noreferrer"
            className="group inline-flex items-center gap-2 bg-sun text-leaf px-7 py-4 font-bold tracking-wide transition-all duration-300 hover:bg-white"
          >
            {u(lang, "hero_cta1")}
            <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </a>
          <a
            data-testid="hero-products-btn"
            href="#products"
            onClick={(e) => {
              e.preventDefault();
              window.__lenis?.scrollTo("#products", { offset: -60 });
            }}
            className="inline-flex items-center gap-2 border border-white/30 text-white px-7 py-4 font-bold tracking-wide transition-all duration-300 hover:border-sun hover:text-sun"
          >
            {u(lang, "hero_cta2")}
          </a>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.2, ease: EASE }}
          className="mt-10 md:hidden"
        >
          <HeroMontage />
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2 text-white/50 text-[10px] tracking-[0.3em] uppercase"
      >
        {u(lang, "scroll")}
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ duration: 1.8, repeat: Infinity }}>
          <ArrowDown size={16} />
        </motion.span>
      </motion.div>
    </section>
  );
}
